import React, { useRef, useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface BusinessImpactSectionProps {
  className?: string;
}

const BusinessImpactSection: React.FC<BusinessImpactSectionProps> = ({ className = '' }) => {
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) setIsInView(true);},
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const impactMetrics = [
  {
    icon: 'ArrowTrendingUpIcon',
    metric: 'Overall Revenue',
    description: 'Total revenue generated across all channels',
    stat: '$123 Million',
    color: 'from-primary to-accent'
  },
  {
    icon: 'UserGroupIcon',
    metric: 'Increase in Conversion Rate in 6 Months',
    description: 'Growth multiplier in conversion performance',
    stat: '2.35 X Growth',
    color: 'from-primary to-accent'
  },
  {
    icon: 'ChartBarIcon',
    metric: 'Cart/dropout Conversions Achieved',
    description: 'Successfully recovered abandoned cart conversions',
    stat: '9%',
    color: 'from-primary to-accent'
  }];


  const commercialDrivers = [
  {
    title: 'Sell More',
    description: 'AI-powered engagement identifies upsell opportunities and accelerates deal closure across every customer touchpoint.',
    icon: 'CurrencyDollarIcon',
    image: '/assets/images/sell-more-1770798793350.png',
    alt: 'Professional business team analyzing growth charts and sales data on modern computer displays'
  },
  {
    title: 'Retain More',
    description: 'Predictive intelligence detects at-risk customers before they churn, enabling proactive retention strategies that work.',
    icon: 'ShieldCheckIcon',
    image: '/assets/images/retain-more-1770798936650.png',
    alt: 'Business professionals collaborating on customer retention strategies with positive data trends'
  },
  {
    title: 'Grow Value Per Customer',
    description: 'Coordinated interactions across channels compound over time, turning one-time buyers into high-value, loyal advocates.',
    icon: 'ArrowPathIcon',
    image: '/assets/images/grow-as-customer-value-1770799041337.png',
    alt: 'Upward trending business growth visualization showing customer value increase over time'
  }];


  const outcomes = [
  { label: 'Higher Conversions', icon: 'ArrowTrendingUpIcon' },
  { label: 'Increase repeat purchases', icon: 'ArrowPathRoundedSquareIcon' },
  { label: 'Grow customer lifetime value', icon: 'ChartBarIcon' },
  { label: 'Scale predictable revenue', icon: 'CurrencyDollarIcon' }];


  return (
    <section ref={sectionRef} className={` bg-white ${className}`}>
      <div className="container mx-auto px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">

          {/* D2C Growth Section */}
          <div
            className={`mb-24 transition-all duration-1000 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>

          </div>

          {/* Commercial Performance */}
          <div
            className={`mb-20 transition-all duration-1000 delay-200 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>

            <div className="text-left mb-14">
              <div className="text-xs font-semibold uppercase tracking-widest mb-6 text-left">
                <span className="font-body text-sm font-semibold tracking-wide text-[rgba(27,180,215,1)]">Commercial Performance</span>
              </div>
              <h2 className="sm:text-5xl font-bold mb-8 max-w-3xl text-5xl text-black text-left">
                This directly impacts your topline.
              </h2>
              <p className="font-body lg:text-lg text-slate-500 max-w-3xl mb-3 text-lg text-left">
                If customer engagement doesn't increase revenue or lifetime value, it doesn't matter.
              </p>
              <p className="font-display font-semibold text-xl lg:text-2xl text-slate-900 text-left">
                UnifyMax is built for commercial outcomes, not vanity CX metrics.
              </p>
            </div>

            {/* Impact Metrics */}
            <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mb-20">
              {impactMetrics.map((metric, index) =>
              <div
                key={index}
                className={`bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:border-[#1bb4d7]/30 hover:shadow-xl transition-all duration-400 group ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ transitionDelay: `${index * 100 + 200}ms` }}>

                  <div className="bg-[#1bb4d7] rounded-xl p-3 inline-block mb-6">
                    <Icon name={metric.icon as any} size={24} variant="solid" className="text-white" />
                  </div>
                  <div className="font-display font-bold text-3xl xl:text-4xl text-slate-900 mb-2 whitespace-nowrap">{metric.stat}</div>
                  <h3 className="font-display font-bold text-lg text-slate-900 mb-2 leading-snug">{metric.metric}</h3>
                  <p className="font-body text-sm text-slate-500 leading-relaxed">{metric.description}</p>
                </div>
              )}
            </div>

            {/* Commercial Drivers */}
            <div>
              <div className="text-center mb-12">
                <h3 className="font-display font-bold text-2xl lg:text-3xl text-slate-900 mb-4">
                  Three Ways UnifyMax Drives Revenue Growth
                </h3>
                <p className="font-body text-base lg:text-lg text-slate-500 max-w-2xl mx-auto">
                  Every feature, every integration, every AI model is designed to move one of these three commercial levers.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                {commercialDrivers.map((driver, index) =>
                <div
                  key={index}
                  className={`bg-white rounded-2xl overflow-hidden border border-slate-100 hover:border-[#1bb4d7]/30 hover:shadow-xl transition-all duration-400 flex flex-col group ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                  style={{ transitionDelay: `${index * 100 + 300}ms` }}>

                    <div className="relative h-64 w-full overflow-hidden bg-slate-100">
                      <AppImage
                      src={driver.image}
                      alt={driver.alt}
                      width={400}
                      height={256}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />

                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/10" />
                    </div>
                    <div className="p-6 lg:p-8 flex flex-col flex-grow">
                      <h4 className="font-display font-bold text-xl text-slate-900 mb-3">
                        {driver.title}
                      </h4>
                      <p className="font-body text-base text-slate-500 leading-relaxed">
                        {driver.description}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default BusinessImpactSection;